import { useEffect, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import { LogOut, FileText, Receipt, MessageSquare, LifeBuoy, Folder, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";
import { SEO_MAP } from "@/lib/seoMap";

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/portal/summary").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return (
    <main className="min-h-[100svh] pt-32 pb-20 grid place-items-center" data-testid="portal-loading">
      <Loader2 className="animate-spin" />
    </main>
  );

  return (
    <main className="min-h-[100svh] pt-28 pb-24 relative" data-testid="portal-page">
      <SEO {...SEO_MAP.portal} />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="overline mb-2">Client Portal</div>
            <h1 className="font-serif text-5xl md:text-6xl tracking-[-0.02em] leading-[1]">
              Welcome,<br /><span className="italic">{user?.name || "Client"}</span>.
            </h1>
          </div>
          <button
            onClick={logout}
            className="h-10 px-4 rounded-full border border-[color:var(--border-strong)] text-[13px] font-medium inline-flex items-center gap-2 hover:bg-[color:var(--surface-elev)] transition-colors"
            data-testid="portal-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-8">
            <PortalCard title="Projects" icon={Folder} testId="portal-projects">
              <div className="divide-y divide-[color:var(--border)]">
                {data.projects.map((p) => (
                  <div key={p.id} className="py-4 flex items-center gap-4">
                    <div className="flex-1">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-[12.5px] text-[color:var(--text-muted)]">{p.status}</div>
                    </div>
                    <div className="w-40 hidden md:block">
                      <div className="h-1.5 rounded-full bg-[color:var(--border)] overflow-hidden">
                        <div className="h-full bg-brand-primary" style={{ width: `${p.progress}%` }} />
                      </div>
                    </div>
                    <span className="font-mono text-[12px] w-10 text-right">{p.progress}%</span>
                  </div>
                ))}
              </div>
            </PortalCard>
          </div>

          <div className="col-span-12 lg:col-span-4 space-y-4">
            <PortalCard title="Invoices" icon={Receipt} testId="portal-invoices">
              {data.invoices.map((i) => (
                <div key={i.id} className="py-2.5 flex items-center justify-between text-[13.5px] border-b border-[color:var(--border)] last:border-b-0">
                  <div>
                    <div>{i.id}</div>
                    <div className="text-[12px] text-[color:var(--text-muted)]">{i.issued}</div>
                  </div>
                  <div className="text-right">
                    <div>£{i.amount_gbp.toLocaleString()}</div>
                    <div className={`text-[11px] ${i.status === "Paid" ? "text-emerald-500" : "text-amber-500"}`}>{i.status}</div>
                  </div>
                </div>
              ))}
            </PortalCard>

            <PortalCard title="Support Tickets" icon={LifeBuoy} testId="portal-tickets">
              {data.tickets.map((t) => (
                <div key={t.id} className="py-2.5 text-[13.5px]">
                  <div>{t.subject}</div>
                  <div className="text-[12px] text-[color:var(--text-muted)]">{t.id} · {t.status} · {t.priority}</div>
                </div>
              ))}
            </PortalCard>

            <PortalCard title="Messages" icon={MessageSquare} testId="portal-messages">
              {data.messages.map((m) => (
                <div key={m.id} className="py-2.5 text-[13.5px]">
                  <div className="font-medium">{m.from}</div>
                  <div className="text-[12.5px] text-[color:var(--text-muted)]">{m.preview}</div>
                </div>
              ))}
            </PortalCard>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickAction label="Files" icon={FileText} />
          <QuickAction label="Domains" icon={Folder} />
          <QuickAction label="Hosting" icon={Folder} />
          <QuickAction label="Maintenance" icon={LifeBuoy} />
        </div>
      </div>
    </main>
  );
}

function PortalCard({ title, icon: Icon, children, testId }) {
  return (
    <div className="card-surface p-6 lg:p-7" data-testid={testId}>
      <div className="flex items-center gap-2.5 mb-5">
        <Icon size={15} className="text-brand-primary" />
        <span className="overline">{title}</span>
      </div>
      {children}
    </div>
  );
}

function QuickAction({ label, icon: Icon }) {
  return (
    <button className="h-24 rounded-2xl border border-[color:var(--border)] hover:border-[color:var(--border-strong)] hover:bg-[color:var(--surface-elev)] transition-colors flex flex-col items-start justify-between p-5 text-left">
      <Icon size={16} />
      <div className="font-medium text-[13.5px]">{label}</div>
    </button>
  );
}
